// src/features/admin/StringListEditor.tsx
// Editable list of plain strings — for the repeated-string AdminSettings
// fields (allowed IPs, notification emails, trusted registries).

import { useState } from 'react';
import { Button, Input } from '@react/components';

export interface StringListEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  addLabel: string;
  emptyLabel: string;
  placeholder?: string;
}

export function StringListEditor({ value, onChange, addLabel, emptyLabel, placeholder }: StringListEditorProps) {
  const [draft, setDraft] = useState('');

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...value, v]);
    setDraft('');
  };

  const remove = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      {value.length === 0 && <p className="text-sm text-muted-foreground">{emptyLabel}</p>}
      {value.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={item} onChange={(e) => onChange(value.map((v, idx) => (idx === i ? e.target.value : v)))} className="flex-1" />
          <Button type="button" variant="ghost" size="sm" aria-label="Remove" onClick={() => remove(i)}>
            <svg className="h-4 w-4 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </Button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button type="button" variant="secondary" size="sm" onClick={add} disabled={!draft.trim()}>{addLabel}</Button>
      </div>
    </div>
  );
}

export default StringListEditor;
